import React, { forwardRef } from "react";
import { cva, VariantProps } from "class-variance-authority";
import clsx from "clsx";
import { HiOutlineExclamationCircle } from "react-icons/hi2";

import Tooltip from "@/components/atoms/Tooltip";

export const sizeVariants = cva(
  "relative flex w-full items-center gap-x-2 rounded-lg border shadow-xs transition duration-75 hover:shadow-sm active:shadow-xs disabled-within:pointer-events-none disabled-within:bg-disabled",
  {
    variants: {
      sizing: {
        xs: "px-3.5 py-2 text-sm",
        sm: "px-3.5 py-2.5 text-sm",
        md: "px-3.5 py-2.5 text-base",
        lg: "px-3.5 py-3 text-base",
        xl: "p-3.5 text-xl",
      },
    },
    defaultVariants: {
      sizing: "sm",
    },
  },
);

export type InputProps = {
  hasError?: boolean;
  leftPlaceholder?: React.ReactNode | string;
  tooltip?: React.ReactNode | string;
};

export type InputHTMLProps = React.InputHTMLAttributes<HTMLInputElement> &
  VariantProps<typeof sizeVariants> &
  InputProps;

const Input = forwardRef<HTMLInputElement, InputHTMLProps>(
  ({ hasError, leftPlaceholder, tooltip, className, disabled, sizing, ...props }, ref) => {
    return (
      <div
        className={clsx(
          sizeVariants({ sizing }),
          hasError ? "border-error-subtle text-fg-error-primary" : "border-primary text-primary",
          className,
        )}
      >
        {leftPlaceholder && (
          <span className="block text-tertiary" data-testid={"left-placeholder"}>
            {leftPlaceholder}
          </span>
        )}
        <input
          ref={ref}
          className={"w-full bg-transparent text-primary outline-none placeholder:text-placeholder"}
          disabled={disabled}
          autoComplete={"off"}
          type={props.type}
          inputMode={props.type === "number" ? "numeric" : undefined}
          pattern={props.type === "number" ? "[0-9]*" : undefined}
          {...props}
        />
        {tooltip && !hasError && <Tooltip children={tooltip} />}
        {hasError && <HiOutlineExclamationCircle className={"h-4.5 w-4.5"} />}
      </div>
    );
  },
);

export default Input;
